import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Drawer,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import { HistorieResponse, AenderungsBatch } from '../../types';
import { historieService } from '../../services/historieService';

interface HistorieTimelineProps {
  entitaetTyp: string;
  entitaetId: number;
  open: boolean;
  onClose: () => void;
}

const FELD_LABELS: Record<string, string> = {
  name: 'Name',
  beschreibung: 'Beschreibung',
  management_summary: 'Management Summary',
  vorbemerkungen: 'Vorbemerkungen',
  status_id: 'Status',
  audit_status_id: 'Audit-Status',
  projektleiter_id: 'Projektleiter',
  kunde_id: 'Kunde',
  personentage: 'Personentage',
  personentage_onsite: 'Personentage On-Site',
  personentage_remote: 'Personentage Remote',
  projekt_typ: 'Projekttyp',
  tagessatz: 'Tagessatz',
  tagessatz_onsite: 'Tagessatz On-Site',
  tagessatz_remote: 'Tagessatz Remote',
  stunden_pro_pt: 'Stunden/PT',
  mitarbeiter_ids: 'Team',
  audit_type_ids: 'Audit-Typen',
  titel: 'Titel',
  kritikalitaet: 'Kritikalität',
  cvss31_score: 'CVSS 3.1 Score',
  cvss31_vector: 'CVSS 3.1 Vector',
  host: 'Host',
  ip_adresse: 'IP-Adresse',
  schwachstellenklasse_id: 'Schwachstellenklasse',
  reporter_id: 'Reporter',
  reviewed_by_id: 'Reviewer',
  tool_ids: 'Tools',
  risikoszenario_ids: 'Risikoszenarien',
  detailbeschreibung: 'Detailbeschreibung',
  allgemeine_beschreibung: 'Allgemeine Beschreibung',
  name_de: 'Name (DE)',
  name_en: 'Name (EN)',
  allgemeine_beschreibung_de: 'Allgemeine Beschreibung (DE)',
  allgemeine_beschreibung_en: 'Allgemeine Beschreibung (EN)',
  sprache: 'Sprache',
  mitigationsmassnahmen: 'Mitigationsmaßnahmen',
  name_des_unternehmens: 'Unternehmen',
  adresse_des_unternehmens: 'Adresse',
  strasse: 'Straße',
  plz: 'PLZ',
  ort: 'Ort',
  email: 'E-Mail',
  rechnungskontakt: 'Rechnungskontakt',
  rechnungsaufteilung_aktiv: 'Rechnungsaufteilung',
  leistungsnachweis_an_rechnung: 'Leistungsnachweis auf Rechnung',
  leistungsnachweis_vorlage: 'Leistungsnachweis-Vorlage',
  aktiv: 'Aktiv',
  rollen_ids: 'Rollen',
  mfa_pflicht: 'MFA-Pflicht',
  status: 'Status',
  datum: 'Datum',
  stunden: 'Stunden',
  mitarbeiter_id: 'Mitarbeiter',
  projekt_id: 'Projekt',
  audit_id: 'Audit',
  rechnung_id: 'Rechnung',
  von: 'Von',
  bis: 'Bis',
  anzahl_tage: 'Anzahl Tage',
  bemerkung: 'Bemerkung',
  admin_bemerkung: 'Admin-Bemerkung',
  genehmigt_von_id: 'Genehmigt von',
  urlaubstage_gesamt: 'Urlaubstage pro Jahr',
  eintrittsdatum: 'Eintrittsdatum',
  urlaub_uebertrag: 'Urlaubsübertrag',
  startdatum: 'Startdatum',
  enddatum: 'Enddatum',
  datei: 'Datei',
  preflight_datum: 'Preflight-Datum',
  rechnungsnummer: 'Rechnungsnummer',
  rechnungsdatum: 'Rechnungsdatum',
  nettobetrag: 'Nettobetrag',
  bruttobetrag: 'Bruttobetrag',
};

const AKTION_CONFIG: Record<string, { label: string; color: 'success' | 'info' | 'error' | 'warning'; icon: React.ReactNode }> = {
  ERSTELLT: { label: 'Erstellt', color: 'success', icon: <AddCircleOutlineIcon fontSize="small" /> },
  GEAENDERT: { label: 'Geändert', color: 'info', icon: <EditIcon fontSize="small" /> },
  GELOESCHT: { label: 'Gelöscht', color: 'error', icon: <DeleteIcon fontSize="small" /> },
  DATEI_HOCHGELADEN: { label: 'Datei hochgeladen', color: 'success', icon: <UploadFileIcon fontSize="small" /> },
  DATEI_GELOESCHT: { label: 'Datei gelöscht', color: 'warning', icon: <DeleteIcon fontSize="small" /> },
};

function formatDateTime(isoString: string): string {
  const d = new Date(isoString);
  return d.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function truncateValue(val: string | undefined | null, maxLen = 80): string {
  if (!val) return '–';
  // Strip HTML tags for display
  const stripped = val.replace(/<[^>]*>/g, '').trim();
  if (stripped.length <= maxLen) return stripped || '–';
  return stripped.substring(0, maxLen) + '…';
}

const BatchItem: React.FC<{ batch: AenderungsBatch }> = ({ batch }) => {
  const theme = useTheme();
  const config = AKTION_CONFIG[batch.aktion] || AKTION_CONFIG.GEAENDERT;

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        '&:not(:last-child)': { mb: 1 },
        '&::before': { display: 'none' },
        borderRadius: '8px !important',
        overflow: 'hidden',
      }}
    >
      <AccordionSummary
        expandIcon={batch.aenderungen.length > 0 && batch.aktion === 'GEAENDERT' ? <ExpandMoreIcon /> : null}
        sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5 } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, flexWrap: 'wrap' }}>
          <Chip
            icon={config.icon as React.ReactElement}
            label={config.label}
            color={config.color}
            size="small"
            variant="outlined"
          />
          <Typography variant="body2" color="text.secondary">
            {batch.geaendert_von_name || `User #${batch.geaendert_von_id}`}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
            {formatDateTime(batch.geaendert_am)}
          </Typography>
        </Box>
      </AccordionSummary>
      {batch.aktion === 'GEAENDERT' && batch.aenderungen.length > 0 && (
        <AccordionDetails sx={{ pt: 0, px: 2, pb: 1.5 }}>
          <Table size="small">
            <TableBody>
              {batch.aenderungen.map((a) => (
                <TableRow key={a.id} sx={{ '& td': { borderBottom: `1px solid ${theme.palette.divider}`, py: 0.5 } }}>
                  <TableCell sx={{ fontWeight: 600, width: 180, whiteSpace: 'nowrap' }}>
                    {FELD_LABELS[a.feld_name || ''] || a.feld_name || '–'}
                  </TableCell>
                  <TableCell sx={{ color: 'error.main' }}>
                    {truncateValue(a.alter_wert)}
                  </TableCell>
                  <TableCell sx={{ width: 30, textAlign: 'center', color: 'text.disabled' }}>→</TableCell>
                  <TableCell sx={{ color: 'success.main' }}>
                    {truncateValue(a.neuer_wert)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AccordionDetails>
      )}
      {(batch.aktion === 'DATEI_HOCHGELADEN' || batch.aktion === 'DATEI_GELOESCHT') && batch.aenderungen.length > 0 && (
        <AccordionDetails sx={{ pt: 0, px: 2, pb: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            {batch.aenderungen[0]?.neuer_wert || batch.aenderungen[0]?.alter_wert || ''}
          </Typography>
        </AccordionDetails>
      )}
    </Accordion>
  );
};

const HistorieTimeline: React.FC<HistorieTimelineProps> = ({
  entitaetTyp,
  entitaetId,
  open,
  onClose,
}) => {
  const [data, setData] = useState<HistorieResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    historieService
      .getHistorie(entitaetTyp, entitaetId)
      .then(setData)
      .catch(() => setError('Historie konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [open, entitaetTyp, entitaetId]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 520, md: 600 }, p: 0 } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>
            Änderungshistorie
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ p: 2.5, overflowY: 'auto', flex: 1 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && data && data.batches.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Keine Änderungen vorhanden.
          </Typography>
        )}

        {!loading && !error && data && data.batches.map((batch) => (
          <BatchItem key={batch.batch_id} batch={batch} />
        ))}
      </Box>
    </Drawer>
  );
};

export default HistorieTimeline;
